import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Rocket,
  TrendingUp,
  TrendingDown,
  Target,
  ShoppingCart,
  Users,
  Eye,
  MousePointer,
  BarChart3,
  Calendar,
  FileText,
  Settings,
  ArrowUpRight,
  ArrowDownRight,
  Globe,
  Search,
  Zap,
  Video,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

const CopiloteDashboard = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copiloteData, setCopiloteData] = useState(null);
  const [missions, setMissions] = useState([]);
  const [meetings, setMeetings] = useState([]);

  useEffect(() => {
    fetchCopiloteData();
  }, []);

  const fetchCopiloteData = async () => {
    try {
      // In real app, fetch from API
      // const res = await api.get('/copilote/dashboard');
      // setCopiloteData(res.data);
      
      // Mock data for now
      setCopiloteData({
        subscription: {
          plan: 'Premium',
          price: 990,
          startDate: '2024-01-15',
          nextBilling: '2025-01-15'
        },
        kpis: {
          roi: { value: 340, trend: 12, unit: '%' },
          avgBasket: { value: 127, trend: 8, unit: '€' },
          conversionRate: { value: 3.4, trend: -0.2, unit: '%' },
          traffic: { value: 12450, trend: 23, unit: '' },
          leads: { value: 89, trend: 15, unit: '' },
          seoPosition: { value: 4, trend: 2, unit: '' }
        },
        objectives: [
          { name: 'Trafic mensuel', target: 15000, current: 12450, unit: 'visites' },
          { name: 'Leads générés', target: 100, current: 89, unit: 'leads' },
          { name: 'Taux de conversion', target: 4.0, current: 3.4, unit: '%' },
          { name: 'Position SEO moyenne', target: 3, current: 4, unit: 'ème' }
        ]
      });
      
      setMissions([
        {
          id: '1',
          name: 'Optimisation SEO Q1',
          status: 'in_progress',
          progress: 65,
          deadline: '2025-03-31',
          objectives: ['Top 3 sur mots-clés principaux', 'Augmenter trafic organique +30%']
        },
        {
          id: '2',
          name: 'Campagne Ads Google',
          status: 'planned',
          progress: 0,
          deadline: '2025-02-15',
          objectives: ['ROI > 300%', '50 leads qualifiés']
        }
      ]);

      // Meeting summaries for the dashboard
      setMeetings([
        {
          id: '1',
          date: '2024-12-20',
          type: 'Point mensuel',
          participants: ['Jean Dupont', 'Marie Martin'],
          summary: 'Revue des KPIs du mois. Objectif trafic atteint à 83%. Décision de lancer la campagne Google Ads en février.',
          actionItems: ['Préparer brief campagne', 'Finaliser landing pages']
        },
        {
          id: '2',
          date: '2024-12-10',
          type: 'Stratégie SEO',
          participants: ['Jean Dupont', 'Paul Expert'],
          summary: 'Analyse des positions SEO. Progression de 2 places sur le mot-clé principal. Nouvelles opportunités identifiées.',
          actionItems: ['Créer 3 articles blog', 'Optimiser pages produits']
        }
      ]);
    } catch (error) {
      console.error('Error fetching copilote data:', error);
    } finally {
      setLoading(false);
    }
  };

  const KPICard = ({ title, value, unit, trend, icon: Icon, color }) => (
    <Card className="card">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-[#64748b]">{title}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
                {typeof value === 'number' ? value.toLocaleString('fr-FR') : value}
              </span>
              <span className="text-sm text-[#64748b]">{unit}</span>
            </div>
          </div>
          <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", color)}>
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={cn(
            "flex items-center gap-1 mt-2 text-sm",
            trend >= 0 ? "text-emerald-600" : "text-red-600"
          )}>
            {trend >= 0 ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : (
              <ArrowDownRight className="w-4 h-4" />
            )}
            <span>{trend >= 0 ? '+' : ''}{trend}% vs mois dernier</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const ObjectiveProgress = ({ objective }) => {
    const progress = Math.min((objective.current / objective.target) * 100, 100);
    const isAchieved = objective.current >= objective.target;
    
    return (
      <div className="p-4 bg-[#f8fafc] rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#334155]">{objective.name}</span>
          <Badge className={isAchieved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
            {objective.current} / {objective.target} {objective.unit}
          </Badge>
        </div>
        <div className="h-2 bg-[#e2e8f0] rounded-full overflow-hidden">
          <div 
            className={cn(
              "h-full rounded-full transition-all",
              isAchieved ? "bg-emerald-500" : progress > 70 ? "bg-amber-500" : "bg-orange-500"
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header with COPILOTE branding */}
        <div className="mb-8 p-6 rounded-2xl bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-[#0f172a] relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl" />
          
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                <Rocket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Mode Copilote</h1>
                <p className="text-[#94a3b8]">Tableau de bord ROI & Performance</p>
              </div>
            </div>
            
            {/* Subscription badge */}
            <div className="flex items-center gap-3 mt-4">
              <Badge className="bg-orange-500/20 text-orange-300 border border-orange-500/30 px-3 py-1">
                <Zap className="w-3 h-3 mr-1" />
                Plan {copiloteData?.subscription?.plan}
              </Badge>
              <span className="text-sm text-[#94a3b8]">
                Prochain renouvellement: {copiloteData?.subscription?.nextBilling ? new Date(copiloteData.subscription.nextBilling).toLocaleDateString('fr-FR') : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        {/* KPI Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <KPICard 
            title="ROI Global"
            value={copiloteData?.kpis?.roi?.value}
            unit="%"
            trend={copiloteData?.kpis?.roi?.trend}
            icon={TrendingUp}
            color="bg-emerald-500"
          />
          <KPICard 
            title="Panier Moyen"
            value={copiloteData?.kpis?.avgBasket?.value}
            unit="€"
            trend={copiloteData?.kpis?.avgBasket?.trend}
            icon={ShoppingCart}
            color="bg-violet-500"
          />
          <KPICard 
            title="Taux Conversion"
            value={copiloteData?.kpis?.conversionRate?.value}
            unit="%"
            trend={copiloteData?.kpis?.conversionRate?.trend}
            icon={Target}
            color="bg-amber-500"
          />
          <KPICard 
            title="Trafic Web"
            value={copiloteData?.kpis?.traffic?.value}
            unit=""
            trend={copiloteData?.kpis?.traffic?.trend}
            icon={Globe}
            color="bg-sky-500"
          />
          <KPICard 
            title="Leads Générés"
            value={copiloteData?.kpis?.leads?.value}
            unit=""
            trend={copiloteData?.kpis?.leads?.trend}
            icon={Users}
            color="bg-rose-500"
          />
          <KPICard 
            title="Position SEO"
            value={copiloteData?.kpis?.seoPosition?.value}
            unit="ème"
            trend={copiloteData?.kpis?.seoPosition?.trend}
            icon={Search}
            color="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Objectives */}
          <Card className="card lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1a365d]">
                <Target className="w-5 h-5 text-orange-500" />
                Objectifs du mois
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {copiloteData?.objectives?.map((obj, idx) => (
                <ObjectiveProgress key={idx} objective={obj} />
              ))}
            </CardContent>
          </Card>

          {/* Active Missions */}
          <Card className="card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#1a365d]">
                <Rocket className="w-5 h-5 text-orange-500" />
                Missions actives
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {missions.map((mission) => (
                <div key={mission.id} className="p-4 bg-[#f8fafc] rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-[#334155]">{mission.name}</h4>
                    <Badge className={cn(
                      "text-xs",
                      mission.status === 'in_progress' ? "bg-cyan-100 text-cyan-700" :
                      mission.status === 'planned' ? "bg-gray-100 text-gray-700" :
                      "bg-emerald-100 text-emerald-700"
                    )}>
                      {mission.status === 'in_progress' ? 'En cours' : 
                       mission.status === 'planned' ? 'Planifié' : 'Terminé'}
                    </Badge>
                  </div>
                  
                  {mission.status === 'in_progress' && (
                    <div className="mb-2">
                      <div className="flex justify-between text-xs text-[#64748b] mb-1">
                        <span>Progression</span>
                        <span>{mission.progress}%</span>
                      </div>
                      <div className="h-1.5 bg-[#e2e8f0] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 rounded-full"
                          style={{ width: `${mission.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div className="text-xs text-[#64748b]">
                    <Calendar className="w-3 h-3 inline mr-1" />
                    Échéance: {new Date(mission.deadline).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              ))}
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => window.location.href = '/copilote/missions'}
              >
                Voir toutes les missions
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Button 
            className="h-auto py-4 flex-col gap-2 bg-white hover:bg-gray-50 text-[#334155] border border-gray-200"
            onClick={() => window.location.href = '/copilote/planning'}
          >
            <Calendar className="w-6 h-6 text-orange-500" />
            <span>Planning</span>
          </Button>
          <Button 
            className="h-auto py-4 flex-col gap-2 bg-white hover:bg-gray-50 text-[#334155] border border-gray-200"
            onClick={() => window.location.href = '/copilote/reports'}
          >
            <FileText className="w-6 h-6 text-emerald-500" />
            <span>Rapports</span>
          </Button>
          <Button 
            className="h-auto py-4 flex-col gap-2 bg-white hover:bg-gray-50 text-[#334155] border border-gray-200"
            onClick={() => window.location.href = '/copilote/analytics'}
          >
            <BarChart3 className="w-6 h-6 text-violet-500" />
            <span>Analytics</span>
          </Button>
          <Button 
            className="h-auto py-4 flex-col gap-2 bg-white hover:bg-gray-50 text-[#334155] border border-gray-200"
            onClick={() => window.location.href = '/copilote/subscription'}
          >
            <Settings className="w-6 h-6 text-amber-500" />
            <span>Abonnement</span>
          </Button>
        </div>

        {/* Meeting Summaries Section */}
        <Card className="card mt-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-[#1a365d]">
                <Video className="w-5 h-5 text-violet-500" />
                Résumés de réunions
              </CardTitle>
              <Button variant="outline" size="sm" className="text-sm">
                Voir tout
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {meetings.map((meeting) => (
                <div key={meeting.id} className="p-4 bg-[#f8fafc] rounded-xl border border-[#e2e8f0]">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="bg-violet-100 text-violet-700">{meeting.type}</Badge>
                        <span className="text-sm text-[#64748b] flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(meeting.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                      <p className="text-xs text-[#94a3b8]">
                        Participants: {meeting.participants.join(', ')}
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-sm text-[#334155] mb-3">{meeting.summary}</p>
                  
                  {meeting.actionItems && meeting.actionItems.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#e2e8f0]">
                      <p className="text-xs font-medium text-[#64748b] mb-2 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Actions à suivre
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {meeting.actionItems.map((item, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-white">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {meetings.length === 0 && (
                <div className="text-center py-8 text-[#64748b]">
                  <Video className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p>Aucune réunion récente</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CopiloteDashboard;
